import { HotelFilterVm, HotelFiltersResponse, ValuesOfHotelFiltersVm } from '@shared/magic-filter-bar/entities/filter-types';
import { isNullOrUndefined } from '@app/util/util';
import { HotelFilterApplier, HotelFilterTypes } from '@shared/hotel-itinerary/hotel-filters';
import { HOTEL_FILTERS_TYPES, NO_ZONE } from '@hotels/hotel-results/constants/filters';
import { getAmmenities } from '@app/mappers/ammenities.mapper';
import { PriceWidgetTypes } from '@flight-results/entities/widget';
import { AmenitiesWidget, PriceWidget } from '@app/entities/flight-results-widget';
import {
  AMENITIES_WIDGET_ICONS, AMENITIES_WIDGET_LABELS, PRICE_WIDGET_ICONS, PRICE_WIDGET_LABELS
} from '@flight-results/constants/filters.constants';
import { CheckboxFilter, PriceRangeFilter } from '@shared/filters/entities/fetch-filters.response';
import { HotelFilterSearchPayload } from '@hotels/hotel-results/entities/hotel-filter-search.payload';
import { RangeFilterPayload } from '@shared/filters/entities/range-filter';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { HotelResultPackagesPayload } from '@packages/packages-result/entities/hotel-results';
import { removeAmpersand } from '@app/pipes/mappers/parse-only-letterss.mapper';
import { getLegendByScore } from '@shared/score-detail/helpers/label-score.helper';

export const STEP_HOTEL_RESULTS = 10;
export const DEFAULT_HOTEL_SORTER = {
  OrderDesc: false,
  OrderCriteria: SorterTypes.Recommended,
};
export const DEFAULT_HOTEL_FILTERS = {
  Ammenities: [],
  Categories: [],
  HotelName: null,
  HotelPriceRanges: [],
  PriceTo: undefined,
  PriceFrom: undefined,
  Zones: [],
  Boards: [],
  Tags: [],
  RefundableType: null,
  TotalDiscountPercentageFrom: undefined,
  CheckinTimes: [],
  Score: [],
  ...DEFAULT_HOTEL_SORTER
};
export const DEFAULT_HOTEL_PAGINATION = {
  ResultCountLowerBound: 0,
  ResultCountUpperBound: 10,
};

type SearchByHotelFilterPayload = HotelResultPackagesPayload | HotelFilterSearchPayload;

export function mapHotelFiltersVm(filters: HotelFiltersResponse): HotelFilterVm[] {
  const filtersMapped = Object.keys(filters)
    .reduce((beforeValue, value) =>
      value !== HotelFilterTypes.HotelPriceRanges ? beforeValue.concat(value) : beforeValue, [])
    .map((key: HotelFilterTypes) => {
      const type = getHotelFilterType(key);
      return {
        type,
        filter: HOTEL_FILTERS_TYPES[type],
        value: mapHotelFilterValues(filters, type),
        order: getOrderByType(type)
      };
    }).filter(filter =>
      !!filter?.type &&
      Array.isArray(filter.value) &&
      filter.value.length > 0
    );

  if (
    (Object.keys(filters).includes(HotelFilterTypes.MinPrice) && !isNullOrUndefined(filters[HotelFilterTypes.MinPrice])) &&
    (Object.keys(filters).includes(HotelFilterTypes.MaxPrice) && !isNullOrUndefined(filters[HotelFilterTypes.MaxPrice]))
  ) {
    filtersMapped.push({
      type: HotelFilterTypes.Price,
      filter: HOTEL_FILTERS_TYPES[HotelFilterTypes.Price],
      value: {
        min: filters[HotelFilterTypes.MinPrice],
        max: filters[HotelFilterTypes.MaxPrice],
      },
      order: getOrderByType(HotelFilterTypes.Price)
    });
  }
  return filtersMapped.filter(filter => !!filter?.type).sort((filter, _filter) => _filter.order - filter.order);
}

export function mapInfoPrice(minPrice: number, maxPrice: number): PriceWidget[] {
  const equalParts = 3;
  const limitPrice = (maxPrice - minPrice) / equalParts;
  const prices = [PriceWidgetTypes.Cheap, PriceWidgetTypes.Middle, PriceWidgetTypes.High];
  return prices.map((price: string) => {
    return {
      'Type': price,
      'Label': PRICE_WIDGET_LABELS[price],
      'Icon': PRICE_WIDGET_ICONS[price],
      'PriceLimit': calculateLimitPrice(price, maxPrice, minPrice, limitPrice)
    };
  });
}

export function mapAmenitiesForWidget(searchAmenities: CheckboxFilter[]): AmenitiesWidget[] {
  const priorityAmenities = ['223', '35', '71', '68'];
  return priorityAmenities.map(code => {
    return searchAmenities.find(amenity => amenity.Text === code);
  }).filter(amenity => !isNullOrUndefined(amenity))
    .map(priorityAmenity => {
      return {
        ...priorityAmenity,
        Icon: AMENITIES_WIDGET_ICONS[priorityAmenity.Text],
        Title: AMENITIES_WIDGET_LABELS[priorityAmenity.Text],
      };
    });
}


export function mapHotelFilterPayload(
  filter: HotelFilterApplier, selected: SearchByHotelFilterPayload
): SearchByHotelFilterPayload {
  let payload = {
    ...selected
  };
  switch (filter.type) {
    case HotelFilterTypes.HotelName:
      payload[HotelFilterTypes.HotelName] = removeAmpersand(filter.value as string);
      payload = mapRefreshHotel(payload);
      break;
    case HotelFilterTypes.Tags:
    case HotelFilterTypes.Zones:
    case HotelFilterTypes.Ammenities:
    case HotelFilterTypes.Boards:
    case HotelFilterTypes.CheckinTimes:
    case HotelFilterTypes.Score:
      payload = hotelSearchCheckboxPayload(filter, selected);
      break;
    case HotelFilterTypes.NonRefundable:
      payload[HotelFilterTypes.RefundableType] = !filter.removable ? filter.value as string || null : null;
      payload = mapRefreshHotel(payload);
      break;
    case HotelFilterTypes.Category:
      payload[HotelFilterTypes.Categories] = (filter.value as CheckboxFilter[]).map(_item => _item.Text);
      payload = mapRefreshHotel(payload);
      break;
    case HotelFilterTypes.HotelPriceRanges:
      const rangeValue = (filter.value as PriceRangeFilter[])[0] || { LowerBound: 0, UpperBound: 0 };
      payload.PriceFrom = rangeValue.LowerBound || 0;
      payload.PriceTo = rangeValue.UpperBound || 0;
      payload = mapRefreshHotel(payload);
      break;
    case HotelFilterTypes.Price:
      const priceValue = !isNullOrUndefined((filter?.value as RangeFilterPayload)?.range) ?
        (filter.value as RangeFilterPayload).range : filter.value;
      if ((priceValue as string[]).length) {
        payload.PriceFrom = Number(priceValue[0]);
        payload.PriceTo = Number(priceValue[1]);
        payload = mapRefreshHotel(payload);
      } else {
        payload.PriceFrom = undefined;
        payload.PriceTo = undefined;
        payload = mapRefreshHotel(payload);
      }
      break;
    case HotelFilterTypes.NextPage:
      payload.ResultCountLowerBound = payload.ResultCountUpperBound;
      payload.ResultCountUpperBound += STEP_HOTEL_RESULTS;
      break;
    case HotelFilterTypes.PromotionalRates:
      payload.TotalDiscountPercentageFrom = filter.value as number;
      break;
    case HotelFilterTypes.MemberRatesOnly:
      payload.MemberRatesOnly = filter.value as boolean;
      break;
    case HotelFilterTypes.PackHours:
      payload[HotelFilterTypes.PackHours] = filter.value;
      break;
  }
  return payload;
}

function mapRefreshHotel(payload: SearchByHotelFilterPayload): SearchByHotelFilterPayload {
  return {
    ...payload,
    ResultCountUpperBound: payload.ResultsCount < STEP_HOTEL_RESULTS ? payload.ResultsCount : STEP_HOTEL_RESULTS,
    ResultCountLowerBound: 0,
    Refresh: true
  };
}

function hotelSearchCheckboxPayload(
  applier: HotelFilterApplier, payload: SearchByHotelFilterPayload
): SearchByHotelFilterPayload {
  const currentFilters = payload[applier.type];
  const value = applier.value;
  return applier.removable ?
    {
      ...payload,
      [applier.type]: applier.clear ? [] : currentFilters.filter(filter => filter !== value)
    } : {
      ...payload,
      [applier.type]: applier.clear ? [value] : [...currentFilters, value]
    };
}

export function hotelFilterNeedsTranslation(type: string): boolean {
  return type as HotelFilterTypes === HotelFilterTypes.NonRefundable || type as HotelFilterTypes === HotelFilterTypes.Category;
}

export function hotelFilterIsNoZoneLabel(item: CheckboxFilter): boolean {
  return item.Text === NO_ZONE;
}

export function getOrderByType(key: string): number {
  switch (key) {
    case HotelFilterTypes.Category:
      return 10;
    case HotelFilterTypes.Price:
      return 9;
    case HotelFilterTypes.HotelName:
      return 8;
    case HotelFilterTypes.Boards:
      return 7;
    case HotelFilterTypes.NonRefundable:
      return 6;
    case HotelFilterTypes.Score:
      return 5;
    case HotelFilterTypes.Zones:
      return 4;
    case HotelFilterTypes.CheckinTimes:
      return 3;
    case HotelFilterTypes.Ammenities:
      return 2;
    case HotelFilterTypes.Tags:
      return 1;
    default:
      return 0;
  }
}

export function mapHotelFilterValues(filters: HotelFiltersResponse, key: HotelFilterTypes): ValuesOfHotelFiltersVm {
  switch (key) {
    case HotelFilterTypes.Ammenities:
      const amenities = getAmmenities();
      if (amenities?.length) {
        return filters[key].map(filter => {
          const transformedValue = amenities.find(amenity => amenity.Code.toString() === filter.Text);
          return {
            ...filter,
            TextTransformed: !isNullOrUndefined(transformedValue) ? transformedValue.Name : null
          };
        });
      } else return filters[key];
    case HotelFilterTypes.PromotionalRates:
      return filters[key].filter(filter => filter.Ocurrence > 0);
    case HotelFilterTypes.HotelName:
      if (filters[HotelFilterTypes.HotelNames]?.length) {
        return [...filters[HotelFilterTypes.HotelNames]].sort();
      } return [];
    case HotelFilterTypes.Score:
      return filters[key]
        .filter(filter => filter.Count > 0) 
        .map(filter => {
          const transformedValue = getLegendByScore(filter.Text);
          return {
            ...filter,
            Label: !isNullOrUndefined(transformedValue) ? transformedValue : null
          };
        });
    default:
      return filters[key];
  }
}

function getHotelFilterType(key: HotelFilterTypes): HotelFilterTypes {
  switch (key) {
    case HotelFilterTypes.HotelNames:
      return HotelFilterTypes.HotelName;
    default:
      return key;
  }
}

function calculateLimitPrice(
  type: string, maxPrice: number, minPrice: number, limitPrice: number
): number[] {
  switch (type) {
    case PriceWidgetTypes.Cheap:
      return [minPrice, (minPrice + limitPrice)];
    case PriceWidgetTypes.Middle:
      return [(minPrice + limitPrice), (maxPrice - limitPrice)];
    case PriceWidgetTypes.High:
      return [(maxPrice - limitPrice), maxPrice];
  }
}

